import React, { useEffect, useImperativeHandle } from 'react'
import { useDispatch } from 'react-redux'
import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'
import { Col, Form, InputGroup, Offcanvas, Row } from 'react-bootstrap'
import { useField, useFormikContext } from 'formik'
import * as Yup from 'yup'
import { camelCase, snakeCase } from 'lodash'
import { useAuth, shouldShowExperimentalFeatures } from '../../auth'
import { modifyArticle } from '../articleAPI'
import { coveringHeadingTypes, handleHeading, isAreaArticleType, normalHeadingTypes } from '../ArticleParse'
import { getSubmitParamForArticleUpsert } from '..'
import { useFormikReset, DirtyEffect } from '../CommonComponents'
import { ArticleNameTitle } from '../ArticleNameTitle'
import { Article, Heading, Layoutable } from '../types'
import {
  fieldNameWithParent,
  createSimpleValidation,
  HeadingCombinationSettingTypeField,
  HeadingItemEditField,
  INVALID_NUMBER_MESSAGE,
  HEADING_BASE_DATA,
  buildInitialValues,
  OrderedHeadingCombinationFields,
  HeadingOrientationField,
  buildArticleHeadingsSubmitProps
} from '../articleHeadings'

type FormValues = ReturnType<typeof buildInitialValues>

interface EnumValues {
  headingOrientations: Record<string, string>
  horizontalHeadingObjectStyleTypes: Record<string, string>
  headingCombinationSettingTypes: Record<string, string>
}

interface SharedProps {
  article: Article
  layoutable: Layoutable
  formEditable: boolean
  enumValues: EnumValues
  showExperimentalFeatures: boolean
  layoutableType: string
}

type EventHandler = {
  onSubmit: (values: FormValues) => Promise<unknown>
}

type FieldChangeCallback = (
  e: React.ChangeEvent<HTMLInputElement>,
  field: unknown,
  meta: unknown,
  helpers?: unknown
) => void

const headingsFromContextValues = (values: FormValues): Heading[] => {
  return Object.entries(values.headings)
    .map((h) => h[1].data)
    .filter((h): h is Heading => h !== undefined)
}

interface HeadingSizeNumberFieldProps {
  id: string
  name: string
  value: string | number
  onChange: React.ChangeEventHandler<HTMLInputElement>
  onBlur: React.FocusEventHandler<HTMLInputElement>
  isValid?: boolean
  isInvalid?: boolean
  disabled?: boolean
  headLabel: string
  tailLabel: string
  error?: string
  placeholder?: string
}

const HeadingSizeNumberField: React.FC<HeadingSizeNumberFieldProps> = ({
  id,
  name,
  value,
  onChange,
  onBlur,
  isValid,
  isInvalid,
  disabled,
  headLabel,
  tailLabel,
  error,
  placeholder
}) => {
  return (
    <>
      <InputGroup.Text>{headLabel}</InputGroup.Text>
      <Form.Control
        id={id}
        type='number'
        min={1}
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        isValid={isValid}
        isInvalid={isInvalid}
        disabled={disabled}
        placeholder={placeholder}
      />
      <InputGroup.Text>{tailLabel}</InputGroup.Text>
      <Form.Control.Feedback type='invalid'>{error}</Form.Control.Feedback>
    </>
  )
}

interface HeadingAreaSettingFieldProps {
  name: string
  formEditable: boolean
  rowsEditable?: boolean
  allowBlankRows?: boolean
  onColumnsChange?: FieldChangeCallback
  onCharactersChange?: FieldChangeCallback
  onRowsChange?: FieldChangeCallback
}

const HeadingAreaSettingField: React.FC<HeadingAreaSettingFieldProps> = ({
  name,
  formEditable,
  rowsEditable,
  allowBlankRows,
  onColumnsChange,
  onCharactersChange,
  onRowsChange
}) => {
  const fieldName = camelCase(name.replace('.', '_'))
  const [field] = useField(fieldNameWithParent('data', name))
  const isCovering = coveringHeadingTypes.has(snakeCase(field.value?.headingType))
  const validationBase = Yup.number().typeError(INVALID_NUMBER_MESSAGE).nonNullable(INVALID_NUMBER_MESSAGE).min(1)
  const verticalValidators = [undefined, createSimpleValidation(validationBase)]

  const [columnsField, columnsMeta, columnsHelpers] = useField({
    name: fieldNameWithParent('data.numberOfOccupyingColumns', name),
    validate: verticalValidators[Number(!isCovering)]
  })

  const [charsField, charsMeta, charsHelpers] = useField({
    name: fieldNameWithParent('data.numberOfOccupyingCharacters', name),
    validate: verticalValidators[Number(isCovering)]
  })

  const blankableValidationBase = Yup.number().typeError(INVALID_NUMBER_MESSAGE).nullable().min(1)
  const rowsValidator = allowBlankRows
    ? createSimpleValidation(blankableValidationBase, (value) => (value === '' ? null : value))
    : createSimpleValidation(validationBase)
  const [rowsField, rowsMeta, rowsHelpers] = useField({
    name: fieldNameWithParent('data.numberOfOccupyingRows', name),
    validate: !isCovering ? rowsValidator : undefined
  })

  useEffect(() => {
    if (field.value === undefined) {
      return
    }

    if (!isCovering) {
      if (columnsField.value === undefined) {
        columnsHelpers.setValue('')
      }
    } else {
      if (charsField.value === undefined) {
        charsHelpers.setValue('')
      }
    }

    if (rowsField.value === undefined) {
      rowsHelpers.setValue('')
    }

    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [field.value])

  if (field.value === undefined) {
    return undefined
  }

  const onColumnsChangeProxy: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    columnsHelpers.setValue(Number(e.target.value))
    if (onColumnsChange !== undefined) {
      onColumnsChange(e, columnsField, columnsMeta, columnsHelpers)
    }
  }
  const onCharactersChangeProxy: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    charsHelpers.setValue(Number(e.target.value))
    if (onCharactersChange !== undefined) {
      onCharactersChange(e, charsField, charsMeta, charsHelpers)
    }
  }
  const onRowsChangeProxy: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const keepBlank = allowBlankRows === true && e.target.value === ''
    rowsHelpers.setValue(keepBlank ? '' : Number(e.target.value))
    if (onRowsChange !== undefined) {
      onRowsChange(e, rowsField, rowsMeta, rowsHelpers)
    }
  }

  const onColumnsBlur: React.FocusEventHandler<HTMLInputElement> = (_e) => {
    // NOTE: placeholder
  }
  const onCharactersBlur: React.FocusEventHandler<HTMLInputElement> = (_e) => {
    // NOTE: placeholder
  }
  const onRowsBlur: React.FocusEventHandler<HTMLInputElement> = (_e) => {
    // NOTE: placeholder
  }

  const isDirty =
    columnsField.value !== columnsMeta.initialValue ||
    charsField.value !== charsMeta.initialValue ||
    rowsField.value !== rowsMeta.initialValue

  const verticalProps: HeadingSizeNumberFieldProps = !isCovering
    ? {
        id: `${fieldName}NumberOfColumnsInput`,
        name: `${fieldName}NumberOfColumnsInput`,
        value: columnsField.value ?? '',
        onChange: onColumnsChangeProxy,
        onBlur: onColumnsBlur,
        isInvalid: columnsMeta.error !== undefined,
        disabled: !formEditable,
        headLabel: '縦',
        tailLabel: '段',
        error: columnsMeta.error
      }
    : {
        id: `${fieldName}NumberOfCharactersInput`,
        name: `${fieldName}NumberOfCharactersInput`,
        value: charsField.value ?? '',
        onChange: onCharactersChangeProxy,
        onBlur: onCharactersBlur,
        isInvalid: charsMeta.error !== undefined,
        disabled: !formEditable,
        headLabel: '縦',
        tailLabel: '字',
        error: charsMeta.error
      }
  const horizontalProps: HeadingSizeNumberFieldProps = {
    id: `${fieldName}NumberOfRowsInput`,
    name: `${fieldName}NumberOfRowsInput`,
    value: rowsField.value ?? '',
    onChange: onRowsChangeProxy,
    onBlur: onRowsBlur,
    isInvalid: rowsMeta.error !== undefined,
    disabled: !(rowsEditable ?? formEditable),
    headLabel: '幅',
    tailLabel: '行幅',
    error: rowsMeta.error,
    placeholder: allowBlankRows === true ? 'ナリ' : undefined
  }

  return (
    <DirtyEffect dirty={isDirty}>
      <Row className='gx-3'>
        <Form.Group as={Col}>
          <InputGroup size='sm' className='align-items-center' hasValidation>
            <HeadingSizeNumberField {...verticalProps} />
          </InputGroup>
        </Form.Group>
        {!isCovering && (
          <Form.Group as={Col}>
            <InputGroup size='sm' className='align-items-center' hasValidation>
              <HeadingSizeNumberField {...horizontalProps} />
            </InputGroup>
          </Form.Group>
        )}
        {isCovering && <Col>&nbsp;</Col>}
      </Row>
    </DirtyEffect>
  )
}

const DesignedHeadingSettingField: React.FC<SharedProps> = (props) => {
  const { formEditable, article, layoutable } = props
  return (
    <>
      <Row className='align-items-center'>
        <Col className='fs-6 fw-bold'>{HEADING_BASE_DATA.horizontal_designed.title}</Col>
        <Col sm={8}>
          <HeadingAreaSettingField name='headings.horizontal_designed' formEditable={formEditable} allowBlankRows />
        </Col>
      </Row>
      <HeadingItemEditField
        name='headings.horizontal_designed'
        article={article}
        layoutable={layoutable}
        formEditable={formEditable}
      />

      <hr className='my-2' />

      <Row className='align-items-center'>
        <Col className='fs-6 fw-bold'>{HEADING_BASE_DATA.vertical_designed.title}</Col>
        <Col sm={8}>
          <HeadingAreaSettingField name='headings.vertical_designed' formEditable={formEditable} />
        </Col>
      </Row>
      <HeadingItemEditField
        name='headings.vertical_designed'
        article={article}
        layoutable={layoutable}
        formEditable={formEditable}
      />
    </>
  )
}

const NormalHeadingSettingField: React.FC<SharedProps> = ({
  layoutable,
  article,
  formEditable,
  enumValues,
  showExperimentalFeatures,
  layoutableType
}) => {
  const isSubLayout = layoutableType === 'SubLayout'
  const context = useFormikContext<FormValues>()
  const [, , rowsHelpers] = useField(fieldNameWithParent('data.numberOfOccupyingRows', 'rootHeading'))
  const headings = context.values.headings
  const basicNormalHeadings = Object.entries(headings)
    .filter(([_name, heading]) => {
      return normalHeadingTypes.has(heading.base.type) && heading.data !== undefined
    })
    .map((item) => item[1])

  const basicNormalHeadingsFirst = basicNormalHeadings?.[0]

  const onColumnsChange: FieldChangeCallback = (e) => {
    const headingNumberOfOccupyingColumns = (e.target as HTMLInputElement).value
    const headings = headingsFromContextValues(context.values)
    const numberOfOccupyingRows = handleHeading({
      headings,
      headingNumberOfOccupyingColumns,
      setting: layoutable.setting
    })
    rowsHelpers.setValue(numberOfOccupyingRows)
  }

  const orientationField =
    basicNormalHeadingsFirst !== undefined ? (
      <HeadingOrientationField
        name='rootHeading'
        enumValues={enumValues}
        layoutable={layoutable}
        formEditable={formEditable}
        basicNormalHeadings={basicNormalHeadings}
        showExperimentalFeatures={showExperimentalFeatures}
      />
    ) : undefined

  return (
    <>
      <Row className='align-items-center'>
        <Col className='fs-6 fw-bold'>見出しサイズ設定</Col>
        <Col sm={8}>
          <HeadingAreaSettingField
            name='rootHeading'
            onColumnsChange={onColumnsChange}
            formEditable={formEditable}
            rowsEditable={!isSubLayout && formEditable}
          />
        </Col>
      </Row>
      {orientationField}

      <hr className='my-2' />

      <Row className='align-items-center'>
        <Col className='fs-6 fw-bold'>{HEADING_BASE_DATA.top_covering.title}</Col>
        <Col sm={8}>
          <HeadingAreaSettingField name='headings.top_covering' formEditable={formEditable} />
        </Col>
      </Row>
      <HeadingItemEditField
        name='headings.top_covering'
        article={article}
        layoutable={layoutable}
        formEditable={formEditable}
      />

      <hr className='my-2' />

      <Row className='align-items-center'>
        <Col className='fs-6 fw-bold'>柱・主・袖見出し</Col>
        {showExperimentalFeatures && (
          <HeadingCombinationSettingTypeField
            layoutable={layoutable}
            enumValues={enumValues}
            formEditable={formEditable}
          />
        )}
      </Row>
      <OrderedHeadingCombinationFields
        article={article}
        layoutable={layoutable}
        formEditable={formEditable}
        showExperimentalFeatures={showExperimentalFeatures}
      />

      <hr className='my-2' />

      <Row className='align-items-center'>
        <Col className='fs-6 fw-bold'>{HEADING_BASE_DATA.bottom_covering.title}</Col>
        <Col sm={8}>
          <HeadingAreaSettingField name='headings.bottom_covering' formEditable={formEditable} />
        </Col>
      </Row>
      <HeadingItemEditField
        name='headings.bottom_covering'
        article={article}
        layoutable={layoutable}
        formEditable={formEditable}
      />
    </>
  )
}

const PreambleHeadingSettingField: React.FC<SharedProps> = ({ layoutable, article, formEditable }) => {
  const context = useFormikContext<FormValues>()
  const [, , rowsHelpers] = useField(fieldNameWithParent('data.numberOfOccupyingRows', 'headings.preamble'))

  const onColumnsChange: FieldChangeCallback = (e) => {
    const headingNumberOfOccupyingColumns = (e.target as HTMLInputElement).value
    const headings = headingsFromContextValues(context.values)
    const numberOfOccupyingRows = handleHeading({
      headings,
      headingNumberOfOccupyingColumns,
      setting: layoutable.setting
    })
    rowsHelpers.setValue(numberOfOccupyingRows)
  }

  return (
    <>
      <Row className='align-items-center'>
        <Col className='fs-6 fw-bold'>{HEADING_BASE_DATA.preamble.title}</Col>
        <Col sm={8}>
          <HeadingAreaSettingField
            name='headings.preamble'
            onColumnsChange={onColumnsChange}
            formEditable={formEditable}
          />
        </Col>
      </Row>
      <HeadingItemEditField
        name='headings.preamble'
        article={article}
        layoutable={layoutable}
        formEditable={formEditable}
      />
    </>
  )
}

const ArticleHeadingsDetailBody: React.FC<SharedProps> = (props) => {
  return (
    <div style={{ width: '100%' }}>
      <h4>カット見出し</h4>
      <div className='ms-2'>
        <DesignedHeadingSettingField {...props} />
      </div>
      <hr />
      <h4>通常見出し</h4>
      <div className='ms-2'>
        <NormalHeadingSettingField {...props} />
      </div>
      <hr />
      <h4>{HEADING_BASE_DATA.preamble.title}</h4>
      <div className='ms-2'>
        <PreambleHeadingSettingField {...props} />
      </div>
    </div>
  )
}

interface Props {
  article: Article
  layoutable: Layoutable
  layoutableId: string | number
  layoutableType: string
  enumValues: EnumValues
  isSelectedLayoutableWritable: boolean
  eventHandlerRef: React.RefObject<EventHandler | null>
  handleChangeArticleSubmit?: unknown
}

export const ArticleHeadingsDetail: React.FC<Props> = ({
  article,
  layoutable,
  layoutableId,
  layoutableType,
  enumValues,
  isSelectedLayoutableWritable,
  eventHandlerRef
}) => {
  const formEditable = isSelectedLayoutableWritable && !article.layoutLocked
  const showExperimentalFeatures = shouldShowExperimentalFeatures(useAuth())

  const context = useFormikContext<Partial<FormValues>>()
  const formikReset = useFormikReset()
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, UnknownAction>>()

  useEffect(() => {
    context.resetForm({ values: buildInitialValues(article) })

    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [])

  useImperativeHandle(eventHandlerRef, () => ({
    onSubmit: async (_values: FormValues) => {
      const newArticle = {
        ...article,
        ...buildArticleHeadingsSubmitProps(context.values as FormValues, layoutable.setting)
      }
      const props = {
        layoutableType,
        layoutableId,
        layoutableLockVersion: layoutable?.lockVersion,
        article: newArticle,
        successAlertBody: '見出しの変更が保存されました',
        loadedBinaryFile: undefined,
        deleteFile: undefined,
        auxiliaryLine: null
      }
      const submitParam = getSubmitParamForArticleUpsert(props) as Parameters<typeof modifyArticle>[0]
      return dispatch(modifyArticle(submitParam)).then(() => {
        formikReset.resetForm({ values: context.values })
      })
    }
  }))

  if ((context.values as Partial<FormValues> | undefined)?.headings === undefined) {
    return undefined
  }

  const sharedProps: SharedProps = {
    article,
    layoutable,
    formEditable: !!formEditable,
    enumValues,
    showExperimentalFeatures,
    layoutableType
  }

  return (
    <>
      <Offcanvas.Header>
        <Offcanvas.Title className='detail-offcanvas-title'>
          見出し
          <ArticleNameTitle article={article} />
        </Offcanvas.Title>
      </Offcanvas.Header>
      <div
        className='flex-grow-1 overflow-auto m-1'
        style={{
          borderStyle: 'solid',
          borderWidth: '1px',
          borderColor: 'var(--bs-border-color)'
        }}
      >
        <Offcanvas.Body className='articleHeadingDetailOffcanvasBody'>
          {!isAreaArticleType(article.articleType) && <ArticleHeadingsDetailBody {...sharedProps} />}
        </Offcanvas.Body>
      </div>
    </>
  )
}
